import type { PaginationMeta } from "@/lib/jsonPagination";

export type StaffReportExtractStatus = "queued" | "parsing" | "extracting" | "combining" | "done" | "failed";

export type StaffReportTemplateProposal = {
  title?: string;
  sections: { key: string; heading: string; description: string; required: boolean; fields?: { name: string; description?: string; required?: boolean }[] }[];
  style?: { heading_case?: string; date_format?: string; bullet_style?: string };
};

export type StaffReportExtractRun = {
  id: number;
  status: StaffReportExtractStatus;
  error_message?: string | null;
  proposal?: StaffReportTemplateProposal | null;
  used_hard_cap?: boolean;
  files: Array<{ id: number; original_filename: string; status: string; error_message?: string | null }>;
};

export type StaffReportDraftRevisionData = {
  id: number;
  revision_number: number;
  instruction: string;
  markdown: string;
  created_at: string;
};

export type StaffReportDraftListItem = {
  id: number;
  name: string;
  updated_at: string;
  stage?: StaffReportStage;
  staff_report_run_id?: number | null;
  discarded_at?: string | null;
  staff_report_template_id?: number | null;
  staff_report_research_set_id?: number | null;
  revision?: StaffReportDraftRevisionData;
};

export type StaffReportStage = "preparation" | "reading_files" | "questions" | "ready_to_write" | "writing" | "ready_to_review" | "needs_attention";
export const reportStageLabels: Record<StaffReportStage, string> = {
  preparation: "Preparation",
  reading_files: "Planning",
  questions: "Needs attention",
  ready_to_write: "Ready to write",
  writing: "Drafting",
  ready_to_review: "Review",
  needs_attention: "Failed",
};

export type StaffReportWorkspaceState = {
  template?: StaffReportTemplateProposal | null;
  answers?: Record<number, string>;
  extract_run_id?: number | null;
};

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
}

async function errorFromResponse(resp: Response) {
  const text = await resp.text();
  if (!text) return `Request failed with ${resp.status}`;
  try {
    const parsed = JSON.parse(text) as { error?: unknown };
    if (typeof parsed.error === "string" && parsed.error) return parsed.error;
  } catch {
    // Response body is not JSON.
  }
  return text;
}

async function errorFromStaffReportResponse(resp: Response): Promise<Error> {
  const text = await resp.text();
  try {
    const json = JSON.parse(text) as { error?: unknown };
    if (typeof json.error === "string" && json.error.length > 0) {
      return new Error(json.error);
    }
  } catch {
    // Use the raw body when it is not JSON.
  }
  return new Error(text || `Request failed with ${resp.status}`);
}

export async function startStaffReportTemplateExtract(params: {
  files: File[];
  municipalityId: number;
}) {
  const form = new FormData();
  for (const file of params.files) form.append("files[]", file);
  form.append("municipality_id", String(params.municipalityId));

  const resp = await fetch("/api/v1/staff_reports/extract_template", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken() },
    body: form,
  });
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<StaffReportExtractRun>;
}

export async function getStaffReportTemplateExtract(id: number) {
  const resp = await fetch(`/api/v1/staff_reports/extract_template/${id}`);
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<StaffReportExtractRun>;
}

export async function startStaffReportRun(params: {
  municipalityId: number;
  researchSetId: number;
  draftId?: number;
  templateId?: number;
  template?: StaffReportTemplateProposal | { title: string; sections: unknown[]; style?: unknown } | null;
}) {
  const resp = await fetch("/api/v1/staff_reports/generate", {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
    body: JSON.stringify({
      municipality_id: params.municipalityId,
      research_set_id: params.researchSetId,
      draft_id: params.draftId,
      template_id: params.templateId,
      template: params.template,
    }),
  });
  if (!resp.ok) throw await errorFromStaffReportResponse(resp);
  return resp.json() as Promise<StaffReportRun>;
}

export async function getStaffReportRun(id: number) {
  const resp = await fetch(`/api/v1/staff_reports/generate/${id}`);
  if (!resp.ok) throw await errorFromStaffReportResponse(resp);
  return resp.json() as Promise<StaffReportRun>;
}

export type StaffReportRunStatus = "queued" | "running" | "done" | "failed";

export type StaffReportRun = {
  id: number;
  status: StaffReportRunStatus;
  stage?: string | null;
  error?: string | null;
  skipped_files: string[];
  generated_report_markdown?: string;
  outline?: { title: string; bullets: string[]; missing_required_fields?: string[] }[];
};

export type StaffReportResearchDocument = {
  id: number;
  original_filename: string;
  extension?: string;
  status: string;
  error_message?: string | null;
  position: number;
};

export type StaffReportResearchQuestion = {
  question: string;
  reason?: string;
};

export type StaffReportResearchWindow = {
  heading?: string | null;
  content?: string;
  position: number;
  filename?: string | null;
  estimated_tokens?: number;
};

export type StaffReportResearchSet = {
  id: number;
  municipality_id: number;
  status: string;
  description?: string | null;
  questions: StaffReportResearchQuestion[];
  answers: Array<{ question: string; answer: string }>;
  window_summary: {
    estimated_tokens?: number;
    crossed_price_line?: boolean;
    windows?: StaffReportResearchWindow[];
  };
  error_message?: string | null;
  documents: StaffReportResearchDocument[];
};

export const RESEARCH_TERMINAL_STATUSES = ["failed", "partial", "needs_input", "done"] as const;
export const RESEARCH_MAX_FILES = 20;
export const RESEARCH_MAX_FILE_BYTES = 50 * 1024 * 1024;

export type StaffReportSourcePreview = StaffReportResearchDocument & {
  parsed_markdown: string | null;
  download_url: string;
};

export async function getResearchDocument(setId: number, documentId: number, municipalityId: number) {
  const url = new URL(`/api/v1/staff_reports/research_sets/${setId}/documents/${documentId}`, window.location.origin);
  url.searchParams.set("municipality_id", String(municipalityId));
  const resp = await fetch(url.toString());
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<StaffReportSourcePreview>;
}

export async function createResearchSet(params: {
  municipalityId: number;
  draftId?: number;
  deferProcessing?: boolean;
  description?: string;
  templateHeadings?: string[];
  files: File[];
  reusedDocumentIds?: number[];
}) {
  const form = new FormData();
  form.append("municipality_id", String(params.municipalityId));
  if (params.draftId) form.append("draft_id", String(params.draftId));
  if (params.deferProcessing) form.append("defer_processing", "true");
  form.append("description", params.description || "");
  for (const heading of params.templateHeadings || []) {
    form.append("template_headings[]", heading);
  }
  for (const file of params.files) form.append("files[]", file);
  for (const id of params.reusedDocumentIds || []) form.append("reused_document_ids[]", String(id));
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch("/api/v1/staff_reports/research_sets", {
    method: "POST",
    headers: { "X-CSRF-Token": csrf },
    body: form,
  });
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<StaffReportResearchSet>;
}

export async function prepareResearchSet(params: {
  id: number;
  municipalityId: number;
  description: string;
  templateHeadings: string[];
}) {
  const resp = await fetch(`/api/v1/staff_reports/research_sets/${params.id}/prepare`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
    body: JSON.stringify({ municipality_id: params.municipalityId, description: params.description, template_headings: params.templateHeadings }),
  });
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<StaffReportResearchSet>;
}

export async function getResearchSet(id: number, municipalityId: number) {
  const url = new URL(`/api/v1/staff_reports/research_sets/${id}`, window.location.origin);
  url.searchParams.set("municipality_id", String(municipalityId));
  const resp = await fetch(url.toString());
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<StaffReportResearchSet>;
}

export async function continueResearchSet(params: {
  id: number;
  municipalityId: number;
  answers?: Array<{ question: string; answer: string }>;
}) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch(`/api/v1/staff_reports/research_sets/${params.id}/continue`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
    body: JSON.stringify({
      municipality_id: params.municipalityId,
      answers: params.answers || [],
    }),
  });
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<StaffReportResearchSet>;
}

export async function createFactsSet(params: { municipalityId: number; name?: string }) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch("/api/v1/staff_reports/facts_sets", {
    method: "POST",
    headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
    body: JSON.stringify({ municipality_id: params.municipalityId, name: params.name }),
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Request failed with ${resp.status}`);
  }
  return resp.json() as Promise<{ id: number; municipality_id: number; name?: string; status: string }>;
}

export async function uploadFactsFiles(params: { setId: number; files: File[] }) {
  const form = new FormData();
  for (const f of params.files) form.append("files[]", f);
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch(`/api/v1/staff_reports/facts_sets/${params.setId}/upload`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrf },
    body: form,
  });
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Request failed with ${resp.status}`);
  }
  return resp.json() as Promise<{ id: number; status: string; documents: Array<{ id: number; original_filename: string; status: string; page_count?: number; pages_processed: number }> }>;
}

export async function getFactsSet(id: number) {
  const resp = await fetch(`/api/v1/staff_reports/facts_sets/${id}`);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Request failed with ${resp.status}`);
  }
  return resp.json() as Promise<{ id: number; status: string; documents: Array<{ id: number; original_filename: string; status: string; page_count?: number; pages_processed: number }> }>;
}

export async function previewRetrievedContext(params: { setId: number; templateSections: any[] }) {
  const url = new URL(`/api/v1/staff_reports/facts_sets/${params.setId}/retrieved_context`, window.location.origin);
  url.searchParams.set("template_sections", JSON.stringify(params.templateSections));
  const resp = await fetch(url.toString());
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Request failed with ${resp.status}`);
  }
  return resp.json() as Promise<{ strategy: string; stats: { pages: number; sections: number; per_section_counts: number[] }; context: string }>;
}

export async function listStaffReportTemplates(params: { municipalityId?: number } = {}) {
  const url = new URL(`/api/v1/staff_reports/templates`, window.location.origin);
  if (params.municipalityId) url.searchParams.set("municipality_id", String(params.municipalityId));
  const resp = await fetch(url.toString());
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || `Request failed with ${resp.status}`);
  }
  return resp.json() as Promise<Array<{
    id: number;
    name: string;
    municipality_id?: number;
    s3_key?: string;
    original_filename?: string;
    template_json: any;
  }>>;
}

export async function createStaffReportTemplate(params: {
  name?: string;
  municipalityId: number;
  templateJson: StaffReportTemplateProposal;
  extractRunId?: number;
  file?: File;
}) {
  const csrf = csrfToken();
  if (params.file) {
    const form = new FormData();
    form.append("file", params.file);
    if (params.name) form.append("name", params.name);
    form.append("municipality_id", String(params.municipalityId));
    form.append("template", JSON.stringify(params.templateJson));
    if (params.extractRunId) form.append("extract_run_id", String(params.extractRunId));
    const resp = await fetch(`/api/v1/staff_reports/templates`, {
      method: "POST",
      headers: { "X-CSRF-Token": csrf },
      body: form,
    });
    if (!resp.ok) throw new Error(await errorFromResponse(resp));
    return resp.json() as Promise<{ id: number; name: string; template_json: StaffReportTemplateProposal }>;
  }

  const resp = await fetch(`/api/v1/staff_reports/templates`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
    body: JSON.stringify({
      name: params.name,
      municipality_id: params.municipalityId,
      template: params.templateJson,
      extract_run_id: params.extractRunId,
    }),
  });
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<{ id: number; name: string; template_json: StaffReportTemplateProposal }>;
}

export async function updateStaffReportTemplate(params: {
  id: number;
  name?: string;
  municipalityId: number;
  templateJson?: StaffReportTemplateProposal;
}) {
  const resp = await fetch(`/api/v1/staff_reports/templates/${params.id}`, {
    method: "PATCH",
    headers: { "X-CSRF-Token": csrfToken(), "Content-Type": "application/json" },
    body: JSON.stringify({
      name: params.name,
      municipality_id: params.municipalityId,
      template: params.templateJson,
    }),
  });
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
  return resp.json() as Promise<{ id: number; name: string; template_json: StaffReportTemplateProposal }>;
}

export async function discardStaffReportTemplate(id: number) {
  const resp = await fetch(`/api/v1/staff_reports/templates/${id}`, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrfToken() },
  });
  if (!resp.ok) throw new Error(await errorFromResponse(resp));
}

const REVISE_POLL_INTERVAL_MS = 2000;
const REVISE_POLL_DEADLINE_MS = 270_000;

type StaffReportReviseRunStatus = "pending" | "processing" | "completed" | "failed";

type StaffReportReviseRunResponse = {
  id: number;
  status: StaffReportReviseRunStatus;
  markdown?: string;
  error?: string;
};

function delay(ms: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function getStaffReportReviseRun(id: number): Promise<StaffReportReviseRunResponse> {
  const resp = await fetch(`/api/v1/staff_reports/revise/${id}`);
  if (!resp.ok) {
    throw await errorFromStaffReportResponse(resp);
  }
  return resp.json() as Promise<StaffReportReviseRunResponse>;
}

async function waitForStaffReportRevise(id: number): Promise<{ markdown: string }> {
  const started = Date.now();
  let lastError: Error | null = null;
  while (Date.now() - started < REVISE_POLL_DEADLINE_MS) {
    let run: StaffReportReviseRunResponse;
    try {
      run = await getStaffReportReviseRun(id);
      lastError = null;
    } catch (error) {
      const message = error instanceof Error ? error.message : "Revision failed";
      if (message === "Revision not found") throw error;
      lastError = error instanceof Error ? error : new Error(message);
      await delay(REVISE_POLL_INTERVAL_MS);
      continue;
    }
    switch (run.status) {
      case "completed":
        if (!run.markdown) {
          throw new Error("Revision failed");
        }
        return { markdown: run.markdown };
      case "failed":
        throw new Error(run.error || "Revision failed");
      case "pending":
      case "processing":
        await delay(REVISE_POLL_INTERVAL_MS);
        break;
      default: {
        const unknown: never = run.status;
        throw new Error(unknown);
      }
    }
  }
  throw lastError ?? new Error("Revision timed out");
}

export async function reviseStaffReport(params: {
  markdown: string;
  instruction: string;
  template?: any;
  municipalityId?: number;
}) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch("/api/v1/staff_reports/revise", {
    method: "POST",
    headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
    body: JSON.stringify({
      markdown: params.markdown,
      instruction: params.instruction,
      template: params.template,
      municipality_id: params.municipalityId,
    }),
  });
  if (!resp.ok) {
    throw await errorFromStaffReportResponse(resp);
  }
  const created = (await resp.json()) as { id?: number };
  if (typeof created.id !== "number") {
    throw new Error("Revision failed");
  }
  return waitForStaffReportRevise(created.id);
}

export async function listStaffReportDrafts(params: { municipalityId: number; page?: number; withSources?: boolean }) {
  const url = new URL(`/api/v1/staff_reports/drafts`, window.location.origin);
  url.searchParams.set("municipality_id", String(params.municipalityId));
  url.searchParams.set("page", String(params.page ?? 1));
  url.searchParams.set("per_page", "50");
  if (params.withSources) url.searchParams.set("with_sources", "true");
  const resp = await fetch(url.toString());
  if (!resp.ok) {
    throw await errorFromStaffReportResponse(resp);
  }
  return resp.json() as Promise<{ drafts: StaffReportDraftListItem[]; pagination: PaginationMeta }>;
}

export async function saveStaffReportDraft(params: {
  name: string;
  markdown?: string;
  municipalityId: number;
  staffReportTemplateId?: number | null;
  staffReportResearchSetId?: number | null;
  facts?: string;
  instruction?: string;
  workspaceState?: StaffReportWorkspaceState;
}) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch(`/api/v1/staff_reports/drafts`, {
    method: "POST",
    headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
    body: JSON.stringify({
      name: params.name,
      markdown: params.markdown,
      municipality_id: params.municipalityId,
      staff_report_template_id: params.staffReportTemplateId,
      staff_report_research_set_id: params.staffReportResearchSetId,
      facts: params.facts,
      instruction: params.instruction,
      workspace_state: params.workspaceState,
    }),
  });
  if (!resp.ok) {
    throw await errorFromStaffReportResponse(resp);
  }
  return resp.json() as Promise<StaffReportDraftListItem>;
}

export async function getStaffReportDraft(id: number) {
  const resp = await fetch(`/api/v1/staff_reports/drafts/${id}`);
  if (!resp.ok) {
    const error = await errorFromStaffReportResponse(resp);
    throw Object.assign(error, { status: resp.status });
  }
  return resp.json() as Promise<StaffReportDraftListItem & { markdown: string | null; facts?: string; municipality_id: number; workspace_state?: StaffReportWorkspaceState }>;
}

export async function updateStaffReportDraft(params: {
  id: number;
  name?: string;
  markdown?: string;
  staffReportTemplateId?: number | null;
  staffReportResearchSetId?: number | null;
  facts?: string;
  instruction?: string;
  workspaceState?: StaffReportWorkspaceState;
}) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch(`/api/v1/staff_reports/drafts/${params.id}`, {
    method: "PATCH",
    headers: { "X-CSRF-Token": csrf, "Content-Type": "application/json" },
    body: JSON.stringify({
      name: params.name,
      markdown: params.markdown,
      staff_report_template_id: params.staffReportTemplateId,
      staff_report_research_set_id: params.staffReportResearchSetId,
      facts: params.facts,
      instruction: params.instruction,
      workspace_state: params.workspaceState,
    }),
  });
  if (!resp.ok) {
    throw await errorFromStaffReportResponse(resp);
  }
  return resp.json() as Promise<StaffReportDraftListItem>;
}

export async function discardStaffReportDraft(id: number) {
  const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
  const resp = await fetch(`/api/v1/staff_reports/drafts/${id}`, {
    method: "DELETE",
    headers: { "X-CSRF-Token": csrf },
  });
  if (!resp.ok) {
    throw await errorFromStaffReportResponse(resp);
  }
}

export async function getDraftRevisions(draftId: number) {
  const resp = await fetch(`/api/v1/staff_reports/drafts/${draftId}/revisions`);
  if (!resp.ok) {
    throw await errorFromStaffReportResponse(resp);
  }
  return resp.json() as Promise<StaffReportDraftRevisionData[]>;
}
